import * as React from 'react'
import useRouter from 'use-react-router'
import { Route, Switch, Redirect } from 'react-router-dom'
import { RouteChildrenProps } from 'react-router'
import {
  faExclamation,
  faAngleLeft,
  faAngleRight
} from '@fortawesome/pro-light-svg-icons'
import { LocalDate } from 'js-joda'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { AuthMetadata } from '@espoon-voltti/nuora-app-common/services/auth'

import '@espoon-voltti/styleguide/main.scss'

import {
  ToasterRoot,
  Toasts,
  useToasts
} from '@espoon-voltti/react-voltti/components/toaster'
import {
  ModalRoot,
  ModalDefaultProvider,
  useModals,
  Modals
} from '@espoon-voltti/react-voltti/components/modal'

import { ErrorMessageCtx } from '@espoon-voltti/react-voltti/components/form/formik-util'
import { AttachmentTranslationsCtx } from '@espoon-voltti/react-voltti/components/attachment-selector'
import { datePickerConfig } from '@espoon-voltti/react-datepicker/datepicker-config'

import '@espoon-voltti/nuora-app-common/styles/datepicker.scss'

import { validationMessage } from '@espoon-voltti/nuora-app-common/assets/validations'
import { localizedMonthName } from '@espoon-voltti/nuora-app-common/assets/calendar'
import { getLocalized } from '@espoon-voltti/nuora-app-common/assets/attachments'

import {
  GlobalModal,
  GlobalToaster
} from '@espoon-voltti/nuora-app-common/globals'
import {
  Auth,
  useAuth
} from '@espoon-voltti/nuora-app-common/services/use-auth'

import { availableLocales, defaultLocale, i18n, t, currentLang } from './i18n'
import { Lang } from './assets/i18n'
import { AppFooter, AppHeader } from './components'
import { A11yReport, StoreLoader, AppView, Home, Landing } from './views'
import { SfiAuthService } from './services/auth'

const authService = new SfiAuthService()
const availableLocalesRegExp = availableLocales.join('|')

const UpdateLanguage = () => {
  const router = useRouter<{ locale: Lang }>()
  const locale = router.match.params.locale
  i18n.changeLanguage(locale)
  return null
}

export function App() {
  const [initializing, setInitializing] = React.useState<boolean>(true)
  const authStore = useAuth()

  const modalStore = useModals()
  GlobalModal.init(modalStore)

  const toastStore = useToasts()
  GlobalToaster.init(toastStore)

  React.useEffect(() => {
    authService
      .getAuth()
      .then((x) => authStore.setUser(x))
      .catch((e) => console.warn('auth store init failed', e))
      .finally(() => setInitializing(false))
  }, [])

  return (
    <datePickerConfig.Provider
      value={{
        formatMonth: (x: LocalDate) =>
          localizedMonthName(currentLang)(x.monthValue() - 1),
        monthSelectPrevBtnLabel: () => <FontAwesomeIcon icon={faAngleLeft} />,
        monthSelectNextBtnLabel: () => <FontAwesomeIcon icon={faAngleRight} />,
        yearSelectPrevBtnLabel: () => <FontAwesomeIcon icon={faAngleLeft} />,
        yearSelectNextBtnLabel: () => <FontAwesomeIcon icon={faAngleRight} />
      }}
    >
      <ErrorMessageCtx.Provider value={validationMessage(currentLang)}>
        <AttachmentTranslationsCtx.Provider value={getLocalized(currentLang)}>
          <Toasts.Provider value={toastStore}>
            <Modals.Provider value={modalStore}>
              {initializing ? null : (
                <main>
                  <Switch>
                    <Route path={`/:locale(${availableLocalesRegExp})`}>
                      <UpdateLanguage />
                      <AppHeader authStore={authStore} />
                      {authStore.isLoggedIn() ? (
                        <AuthenticatedContent user={authStore.user!} />
                      ) : (
                        <Landing />
                      )}
                      <AppFooter />
                    </Route>
                    <Route path="/accessibility">
                      <AppHeader authStore={authStore} />
                      <A11yReport />
                      <AppFooter />
                    </Route>

                    <Route path="/dummy">{''}</Route>

                    <Route path="*">
                      <Redirect to={`/${defaultLocale}/`} />
                    </Route>
                  </Switch>

                  <ModalDefaultProvider
                    dataQa="promptModal"
                    icon={faExclamation}
                    type={'warning'}
                    confirmButtonLabel={t(['common', 'ok'])}
                    cancelButtonLabel={t(['common', 'cancel'])}
                  >
                    <ModalRoot />
                  </ModalDefaultProvider>
                  <ToasterRoot />
                </main>
              )}
            </Modals.Provider>
          </Toasts.Provider>
        </AttachmentTranslationsCtx.Provider>
      </ErrorMessageCtx.Provider>
    </datePickerConfig.Provider>
  )
}

function AuthenticatedContent({ user }: { user: AuthMetadata }) {
  return (
    <Auth.Provider value={user}>
      <StoreLoader>
        <Switch>
          <Route exact path={'/:locale'}>
            <Home />
          </Route>
          <Route
            exact
            path={[
              '/:locale/application/:appId',
              '/:locale/application/:appId/signing'
            ]}
          >
            {(route: RouteChildrenProps<{ appId: string }>) => (
              <AppView appId={route.match!.params.appId} />
            )}
          </Route>
        </Switch>
      </StoreLoader>
    </Auth.Provider>
  )
}
